/**
 * A dealer's alerts, one row each: name, the problems it carries as severity
 * pills, who gets it, when, whether it is on, when it last went out, and the
 * actions. Header and zebra styling match components/audit/AuditTable.tsx
 * (headerCellStyle ~205, dataCellStyle ~238) via GroupVehicleTable's copies.
 */
import React, { useState } from "react"

import { AuditAlert, AuditAlertProblemOption } from "../../../api/auditAlerts"
import { THEME } from "../../../config/theme"
import { parseUtcTimestamp } from "../../../utils/parseUtcTimestamp"
import { formatSendTime, labelFor, severityFor } from "./alertFormatting"

interface Props {
  alerts: AuditAlert[]
  options: AuditAlertProblemOption[]
  /** The alert whose Send now is in flight, so the button can say so. */
  sendingId: number | null
  onEdit: (alert: AuditAlert) => void
  onDelete: (alert: AuditAlert) => void
  onSendNow: (alert: AuditAlert) => void
  onToggleEnabled: (alert: AuditAlert) => void
}

const HEADERS = ["Alert", "Store", "Problems", "Recipients", "Sends", "Status", "Last sent", "Actions"]

const headerCellStyle: React.CSSProperties = {
  background: THEME.auditTable.headerBackground,
  color: THEME.colors.white,
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textAlign: "left",
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  whiteSpace: "nowrap",
}

const dataCellStyle = (background: string): React.CSSProperties => ({
  fontSize: 13,
  padding: `${THEME.spacing.sm} ${THEME.spacing.sm}`,
  borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
  verticalAlign: "top",
  background,
})

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>
const SEVERITY_SOFT = THEME.problemDisplay.severitySoft as Record<string, string>

const pillStyle = (severity: string): React.CSSProperties => ({
  display: "inline-block",
  borderRadius: THEME.borderRadius.pill,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.2px",
  padding: "2px 9px",
  marginRight: 4,
  marginBottom: 4,
  background: SEVERITY_SOFT[severity] ?? SEVERITY_SOFT.warning,
  color: SEVERITY_COLORS[severity] ?? SEVERITY_COLORS.warning,
})

// Matches GroupFilterBar's Clear button and WorklistGroup's Retry — the small
// action offered inside a data surface.
const smallButtonStyle: React.CSSProperties = {
  padding: "4px 10px",
  fontSize: 12,
  fontFamily: THEME.fonts.primary,
  borderRadius: THEME.borderRadius.sm,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
  marginRight: 6,
  marginBottom: 4,
}

export default function AuditAlertsTable({
  alerts, options, sendingId, onEdit, onDelete, onSendNow, onToggleEnabled,
}: Props) {
  // Delete asks twice, inline: the first click turns the button into a
  // confirmation, the second deletes. No browser dialog, nothing to dismiss.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null)

  if (alerts.length === 0) {
    return (
      <div style={{ padding: THEME.spacing.lg, fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
        No alerts yet. Create one to start getting a morning email about the problems you pick.
      </div>
    )
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            {HEADERS.map((header) => (
              <th key={header} style={headerCellStyle}>{header.toUpperCase()}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {alerts.map((alert, index) => {
            const zebra = index % 2 === 0 ? THEME.auditTable.zebraLight : THEME.auditTable.zebraMain
            const confirming = confirmingDeleteId === alert.id
            return (
              <tr key={alert.id}>
                <td style={{ ...dataCellStyle(zebra), fontWeight: 600 }}>{alert.name}</td>
                <td style={{ ...dataCellStyle(zebra), whiteSpace: "nowrap" }}>
                  {alert.rooftop_label ?? "All stores"}
                </td>
                <td style={dataCellStyle(zebra)}>
                  {alert.problems.map((problem) => (
                    <span key={problem.slug} style={pillStyle(severityFor(problem, options))}>
                      {labelFor(problem, options)}
                    </span>
                  ))}
                </td>
                <td style={dataCellStyle(zebra)}>{alert.recipients.join(", ")}</td>
                <td style={{ ...dataCellStyle(zebra), whiteSpace: "nowrap" }}>{formatSendTime(alert.hour, alert.minute)}</td>
                <td style={dataCellStyle(zebra)}>
                  <span style={pillStyle(alert.is_enabled ? "expected" : "cleanup")}>
                    {alert.is_enabled ? "ENABLED" : "PAUSED"}
                  </span>
                </td>
                <td style={{ ...dataCellStyle(zebra), whiteSpace: "nowrap", color: THEME.colors.neutral.light.textSecondary }}>
                  {alert.last_sent_at
                    ? parseUtcTimestamp(alert.last_sent_at).toLocaleString([], {
                        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                      })
                    : "Never"}
                </td>
                <td style={{ ...dataCellStyle(zebra), whiteSpace: "nowrap" }}>
                  <button
                    type="button"
                    style={smallButtonStyle}
                    disabled={sendingId === alert.id}
                    onClick={() => onSendNow(alert)}
                  >
                    {sendingId === alert.id ? "Sending…" : "Send now"}
                  </button>
                  <button type="button" style={smallButtonStyle} onClick={() => onEdit(alert)}>
                    Edit
                  </button>
                  <button type="button" style={smallButtonStyle} onClick={() => onToggleEnabled(alert)}>
                    {alert.is_enabled ? "Pause" : "Enable"}
                  </button>
                  {confirming ? (
                    <>
                      <button
                        type="button"
                        style={{ ...smallButtonStyle, color: THEME.colors.semantic.danger, borderColor: THEME.colors.semantic.danger }}
                        onClick={() => { setConfirmingDeleteId(null); onDelete(alert) }}
                      >
                        Confirm delete
                      </button>
                      <button type="button" style={smallButtonStyle} onClick={() => setConfirmingDeleteId(null)}>
                        Keep
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      style={{ ...smallButtonStyle, color: THEME.colors.semantic.danger }}
                      onClick={() => setConfirmingDeleteId(alert.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
